import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import * as d3 from 'd3';
import React from 'react';
import {noop} from 'underscore';

import {NucleotideType} from 'biojs/BioConstants';
import {SequenceType} from 'constants/SequenceConstants';
import {SequenceType as GQLSequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {COMPLEX_POLYMER_FAKE_SEQUENCE_ID} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  AXIS_HEIGHT,
  FONT_SIZE,
  getWidthForBaseCount,
  LETTER_SPACING,
  StaticAxis,
  StaticBases,
} from 'modbio/polymer-maps/OligoMap';
import {
  ModifiedNucleotidesVisualization,
  modifiedNucleotideVisualizationHeight,
  modifiedNucleotideVMargin,
  PHOSPHATE_HEIGHT,
  PHOSPHATE_WIDTH,
} from 'molbio/maps/linear/ModifiedNucleotides';
import Type from 'molbio/maps/Type';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {ModifiedNucleotidesLegendContents} from 'molbio/monomers/ModifiedNucleotidesLegend';
import {MONOMER_QUERY} from 'molbio/monomers/MonomerConstants';
import {ModifiedNucleotide} from 'molbio/monomers/NucleotidesTable';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import {
  OligoConjugateOligoMapQuery,
  OligoConjugateOligoMapQueryVariables,
} from './__generated/OligoConjugateOligoMapQuery';
import {ELEMENT_MARGIN} from './ComplexPolymerMapUtil';

export const OLIGO_CONJUGATE_OLIGO_MAP_QUERY = gql`
  query OligoConjugateOligoMapQuery($id: StrictString!) @skipAudit @engComponent(name: MODIFIED_BIOLOGICS) {
    sequence(id: $id) {
      ... on SequenceNode {
        id
        bases
        nucleotideType
        modifiedNucleotides {
          sugarMonomerId
          baseMonomerId
          phosphateMonomerId
          position
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface QueryingOligoConjugateOligoMapProps {
  oligoId: string;
}

/**
 * Component that displays only the backbone oligo of an oligo conjugate. This does NOT display conjugates and
 * should only be used for an oligo conjugate that does not have associated structure data, for example, if it
 * has not yet been created.
 */
export function QueryingOligoConjugateOligoMap({oligoId}: QueryingOligoConjugateOligoMapProps): JSX.Element {
  return (
    <BenchlingQuery<OligoConjugateOligoMapQuery, OligoConjugateOligoMapQueryVariables>
      query={OLIGO_CONJUGATE_OLIGO_MAP_QUERY}
      renderLoading={() => {
        <div className={css(styles.emptyOligoMap)}>
          <HorizontalThrobber />
        </div>;
      }}
      variables={{id: oligoId}}
    >
      {(data) => {
        const oligo = data.sequence;
        if (isInaccessible(oligo)) {
          return <div className={css(styles.emptyOligoMap)}>You do not have access to view this oligo</div>;
        }

        const {bases, modifiedNucleotides, nucleotideType} = oligo;
        if (bases.length === 0) {
          return (
            <div className={css(styles.emptyOligoMap)}>
              Structure cannot be previewed because the selected oligo has no bases
            </div>
          );
        }

        return (
          <OligoConjugatePolymerMap
            bases={bases}
            modifiedNucleotides={modifiedNucleotides}
            nucleotideType={nucleotideType}
            forCreate={true}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export interface OligoConjugatePolymerMapProps {
  bases: string;
  modifiedNucleotides: Array<ModifiedNucleotide>;
  nucleotideType: NucleotideType;
  forCreate: boolean;
}

export default function OligoConjugatePolymerMap({
  bases,
  modifiedNucleotides,
  nucleotideType,
  forCreate,
}: OligoConjugatePolymerMapProps): JSX.Element {
  const basesWidth = getWidthForBaseCount(bases.length);
  const xFn = d3.scaleLinear().clamp(true).domain([0, bases.length]).range([0, basesWidth]);
  const type = new Type(FONT_SIZE, LETTER_SPACING);

  // Each element in the polymer map will be placed immediately after the element before it, with adjustments
  // for whitespace/margins
  const basesYTransform = -modifiedNucleotideVMargin(xFn, 0, 0) + PHOSPHATE_HEIGHT;
  const axisYTransform = basesYTransform + modifiedNucleotideVisualizationHeight(xFn, 0, 0) + ELEMENT_MARGIN;
  const polymerHeight = axisYTransform + AXIS_HEIGHT;

  // The total width of the polymer is the width of the bases and any trailing phosphate
  const width = basesWidth + PHOSPHATE_WIDTH;

  return (
    <div
      {...(!forCreate && {className: css(styles.oligoMapWithLegend)})}
      {...getTestProps({component: 'OligoConjugatePolymerMap', element: 'container'})}
    >
      <div
        className={
          forCreate ? classNames(css(styles.wrapper), css(styles.oligoMapForCreate)) : css(styles.wrapper)
        }
        {...getTestProps({component: 'OligoConjugatePolymerMap', element: 'root'})}
      >
        <svg
          style={{width, height: polymerHeight}}
          {...getTestProps({component: 'OligoConjugatePolymerMap', element: 'svg'})}
        >
          <g
            id="bases"
            transform={`translate(0,${basesYTransform})`}
            {...getTestProps({component: 'OligoConjugatePolymerMap', element: 'bases'})}
          >
            <BenchlingQuery<MonomerQuery>
              query={MONOMER_QUERY}
              renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
            >
              {(monomerData) => (
                <ModifiedNucleotidesVisualization
                  bases={bases}
                  sequenceMods={modifiedNucleotides}
                  monomers={monomerData.monomers}
                  isOligo={true}
                  nucleotideType={nucleotideType}
                  xFn={xFn}
                  sliceStart={0}
                  sliceEnd={bases.length}
                  layout={{vMargin: 0}}
                  boxHeightToDNA={0}
                  showNucleotideStructures={true}
                  keyPrefix="complexPolymerMap"
                  displayBasesAsNatural={false}
                />
              )}
            </BenchlingQuery>
            <StaticBases
              bases={bases}
              modifiedNucleotides={modifiedNucleotides}
              nucleotideType={nucleotideType}
              sequenceType={SequenceType.OLIGO}
              fontSize={type.fontSize}
              letterSpacing={type.letterSpacing}
              vMargin={10}
              isTemplate={false}
            />
          </g>
          <g
            id="axis"
            transform={`translate(0,${axisYTransform})`}
            {...getTestProps({component: 'OligoConjugatePolymerMap', element: 'axis'})}
          >
            <StaticAxis
              basesLength={bases.length}
              charWidth={type.charWidth()}
              xFn={xFn}
              vMargin={0}
              tickSpacing={2}
            />
          </g>
        </svg>
      </div>
      <div
        className={
          forCreate ? css(styles.legend) : classNames(css(styles.legend), css(styles.legendMaxWidth))
        }
        {...getTestProps({component: 'OligoConjugatePolymerMap', element: 'legend'})}
      >
        <div {...getTestProps({component: 'ModifiedNucleotidesLegendContents', element: 'root'})}>
          <ModifiedNucleotidesLegendContents
            // The sequence ID would be used to query for the oligo structure if needed, but since we provide
            // the structure here, the sequence ID will not be used
            sequenceId={COMPLEX_POLYMER_FAKE_SEQUENCE_ID}
            seqMods={modifiedNucleotides}
            bases={bases}
            nucleotideType={nucleotideType}
            sequenceType={GQLSequenceType.oligo}
            onUpdateHoverState={noop}
          />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    fontFamily: FONT_FAMILY_MONOSPACED,
    shapeRendering: 'crispEdges',
  },
  oligoMapForCreate: {
    height: '130px',
    border: `1px solid ${Color.GRAY_300}`,
    borderRadius: '4px',
    overflowX: 'scroll',
    padding: '35px 15px 0px 15px',
    marginBottom: '10px',
  },
  legend: {
    marginLeft: '-20px',
    width: '100%',
  },
  legendMaxWidth: {
    maxWidth: '600px',
  },
  emptyOligoMap: {
    height: '130px',
    border: `1px solid ${Color.GRAY_50}`,
    borderRadius: '4px',
    marginBottom: '10px',
    background: Color.GRAY_50,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: Color.GRAY_500,
  },
  oligoMapWithLegend: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: 'fit-content',
  },
});
